'use client'

import * as PopoverPrimitive from '@radix-ui/react-popover'
import * as React from 'react'

import { cn } from '../cn'

/**
 * Radix modal dialogs/sheets mount a scroll lock (react-remove-scroll) that
 * cancels wheel/touch scrolling everywhere outside the dialog's own subtree.
 * Popover content portals to <body> — outside that subtree — so a dropdown
 * opened from inside a dialog renders a scrollbar but ignores the wheel.
 * A MODAL popover mounts its own (innermost, therefore active) scroll lock,
 * which re-enables scrolling of its content. Detect the lock and default to
 * modal in that case; explicit `modal` props always win.
 */
export function isBodyScrollLocked(): boolean {
  return typeof document !== 'undefined' && document.body.hasAttribute('data-scroll-locked')
}

function Popover({ modal, onOpenChange, ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  // Sampled via state (not read inline) so UNCONTROLLED popovers re-render
  // with the right modality when they open — their first render happens
  // before the surrounding dialog's lock effect has stamped the body.
  const [locked, setLocked] = React.useState(isBodyScrollLocked)
  return (
    <PopoverPrimitive.Root
      modal={modal ?? locked}
      onOpenChange={(next) => {
        if (next) {
          setLocked(isBodyScrollLocked())
        }
        onOpenChange?.(next)
      }}
      {...props}
    />
  )
}

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverAnchor = PopoverPrimitive.Anchor

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, collisionPadding = 8, onInteractOutside, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      onInteractOutside={(e) => {
        // Keep this popover open when the interaction originates inside
        // another Radix-portaled popper (e.g. a SearchableSelect dropdown
        // hosted inside us). The nested popover manages its own dismiss.
        const target = e.target as HTMLElement | null
        const popper = target?.closest('[data-radix-popper-content-wrapper]')
        const self = (e.currentTarget as HTMLElement).closest('[data-radix-popper-content-wrapper]')
        if (popper && popper !== self) {
          e.preventDefault()
          return
        }
        onInteractOutside?.(e)
      }}
      className={cn(
        'max-h-[var(--radix-popover-content-available-height)] max-w-[calc(100vw-16px)] overflow-y-auto',
        'z-50 w-72 rounded-lg border bg-popover/95 backdrop-blur-lg p-4 text-popover-foreground shadow-lg outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
))
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger }
